import { useState } from 'react';
import ActivityIcon from '@mui/icons-material/AssessmentRounded';
import DevicesIcon from '@mui/icons-material/DevicesRounded';
import HAIcon from '@mui/icons-material/SensorsRounded';
import HomeIcon from '@mui/icons-material/HomeWorkRounded';
import InventoryIcon from '@mui/icons-material/Inventory2Rounded';
import LogoutIcon from '@mui/icons-material/LogoutRounded';
import MemoryIcon from '@mui/icons-material/PsychologyRounded';
import MenuIcon from '@mui/icons-material/MenuRounded';
import PeopleIcon from '@mui/icons-material/PeopleRounded';
import ScenesIcon from '@mui/icons-material/AutoAwesomeRounded';
import SettingsIcon from '@mui/icons-material/SettingsRounded';
import SpeedIcon from '@mui/icons-material/SpeedRounded';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

const DRAWER_WIDTH = 240;

const navItems = [
  { label: 'Dashboard', value: 'dashboard', icon: <SpeedIcon fontSize="small" />, href: '/' },
  { label: 'Users', value: 'users', icon: <PeopleIcon fontSize="small" />, href: '/users' },
  { label: 'Homes', value: 'homes', icon: <HomeIcon fontSize="small" />, href: '/homes' },
  { label: 'Device Catalog', value: 'device-catalog', icon: <DevicesIcon fontSize="small" />, href: '/device-catalog' },
  { label: 'Device Registry', value: 'device-registry', icon: <InventoryIcon fontSize="small" />, href: '/device-registry' },
  { label: 'HA Monitor', value: 'ha-monitor', icon: <HAIcon fontSize="small" />, href: '/ha-monitor' },
  { label: 'Activity Logs', value: 'activity-logs', icon: <ActivityIcon fontSize="small" />, href: '/activity-logs' },
  { label: 'Scenes', value: 'scenes', icon: <ScenesIcon fontSize="small" />, href: '/scenes' },
  { label: 'Memory', value: 'memory', icon: <MemoryIcon fontSize="small" />, href: '/memory' },
  { label: 'Settings', value: 'settings', icon: <SettingsIcon fontSize="small" />, href: '/settings' }
];

function getActiveNav(pathname: string) {
  if (pathname.startsWith('/users')) return 'users';
  if (pathname.startsWith('/homes')) return 'homes';
  if (pathname.startsWith('/device-catalog')) return 'device-catalog';
  if (pathname.startsWith('/device-registry')) return 'device-registry';
  if (pathname.startsWith('/ha-monitor')) return 'ha-monitor';
  if (pathname.startsWith('/activity-logs')) return 'activity-logs';
  if (pathname.startsWith('/scenes')) return 'scenes';
  if (pathname.startsWith('/memory')) return 'memory';
  if (pathname.startsWith('/settings')) return 'settings';
  return 'dashboard';
}

function SidebarContent({ onNavigate }: { onNavigate: (href: string) => void }) {
  const location = useLocation();
  const activeNav = getActiveNav(location.pathname);
  const logout = useAuthStore((s) => s.logout);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" color="primary.main" fontWeight={700} letterSpacing={1}>
          ADMIN PORTAL
        </Typography>
        <Typography variant="caption" color="text.secondary">Smart Home Platform</Typography>
      </Box>

      <List dense sx={{ flex: 1, px: 1, py: 1.5 }}>
        {navItems.map((item) => {
          const active = activeNav === item.value;
          return (
            <ListItemButton
              key={item.value}
              selected={active}
              onClick={() => onNavigate(item.href)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  color: 'primary.main',
                  '& .MuiListItemIcon-root': { color: 'primary.main' }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2', fontWeight: active ? 600 : 400 }} />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
          <Typography variant="body2" noWrap>{user?.email}</Typography>
          <Chip label="Admin" size="small" color="primary" sx={{ width: 'fit-content' }} />
        </Stack>
        <ListItemButton
          onClick={() => { logout(); navigate('/login'); }}
          sx={{ borderRadius: 2, px: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ variant: 'body2' }} />
        </ListItemButton>
      </Box>
    </Box>
  );
}

export function AdminShell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentPage = navItems.find((n) => n.value === getActiveNav(location.pathname));

  const drawerSx = {
    width: DRAWER_WIDTH,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
      width: DRAWER_WIDTH,
      boxSizing: 'border-box',
      bgcolor: 'background.paper',
      borderRight: '1px solid',
      borderColor: 'divider'
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Desktop sidebar */}
      <Drawer variant="permanent" sx={{ ...drawerSx, display: { xs: 'none', md: 'block' } }} open>
        <SidebarContent onNavigate={navigate} />
      </Drawer>

      {/* Mobile sidebar */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ ...drawerSx, display: { xs: 'block', md: 'none' } }}
      >
        <SidebarContent onNavigate={(href) => { setMobileOpen(false); navigate(href); }} />
      </Drawer>

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 1, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" fontWeight={600}>{currentPage?.label || 'Admin Portal'}</Typography>
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flex: 1, p: { xs: 2, md: 3 }, overflow: 'auto' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
